import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Download, Share2, Play, Pause, Volume2, SkipBack, SkipForward, Eye, Heart, MessageCircle } from 'lucide-react';
import DownloadPaywall from '../components/DownloadPaywall';
import ShareModal from '../components/ShareModal';
import SignupInviteModal from '../components/SignupInviteModal';
import ContributingArtistsDisplay from '../components/ContributingArtistsDisplay';
import RichDescription from '../components/RichDescription';
import MetadataDisplay from '../components/MetadataDisplay';
import trackingService from '../services/tracking';
import { useAuth } from '../context/AuthContext';
import { updateAudioMeta } from '../utils/metaUtils';
import api from '../services/api';

const AudioViewer = () => {
    const { uploadId } = useParams();
    const navigate = useNavigate();
    const { isAuthenticated, user } = useAuth();
    const audioRef = useRef(null);
    const [audio, setAudio] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);
    const [volume, setVolume] = useState(1);
    const [downloadPaywall, setDownloadPaywall] = useState({ isOpen: false, audio: null });
    const [shareModal, setShareModal] = useState({ isOpen: false, audio: null });
    const [playCount, setPlayCount] = useState(0);
    const [comments, setComments] = useState([]);
    const [loadingComments, setLoadingComments] = useState(false);
    const [newComment, setNewComment] = useState('');
    const [submittingComment, setSubmittingComment] = useState(false);
    const [signupInvite, setSignupInvite] = useState({ isOpen: false, contentType: 'audio' });

    useEffect(() => {
        const fetchAudio = async () => {
            try {
                // First, try to get the specific audio with user context
                let audioData = null;
                
                // Try fetching specific audio data with user context (includes like status)
                try {
                    const specificResponse = await api.get(`/upload/${uploadId}`);
                    if (specificResponse.data.success) {
                        audioData = specificResponse.data.data;
                    }
                } catch (specificError) {
                    console.log('Specific audio fetch failed, trying public list:', specificError);
                }
                
                // Fallback to public list if specific fetch failed
                if (!audioData) {
                    const response = await api.get('/upload/public/audio');
                    
                    if (response.data.success && response.data.data.length > 0) {
                        audioData = response.data.data.find(upload => upload.id == uploadId);
                    }
                }
                
                if (audioData) {
                    setAudio(audioData);
                    setPlayCount(audioData.play_count || 0);
                    
                    // Update page meta tags for SEO and social sharing
                    updateAudioMeta(audioData).catch(console.error);
                    
                    // If we don't have user like data and user is authenticated, fetch it separately
                    if (isAuthenticated && typeof audioData.user_has_liked === 'undefined') {
                        try {
                            const likeStatusResponse = await api.get(`/upload/${uploadId}/like-status`);
                            if (likeStatusResponse.data.success) {
                                setAudio(prev => ({
                                    ...prev,
                                    user_has_liked: likeStatusResponse.data.data.liked,
                                    like_count: likeStatusResponse.data.data.likeCount
                                }));
                            }
                        } catch (likeError) {
                            console.log('Like status fetch failed:', likeError);
                            // If this endpoint doesn't exist, we'll continue without like status
                        }
                    }
                    
                    // Track page view
                    trackingService.trackPageView('audio', uploadId);
                } else {
                    setError('Audio not found');
                }
            } catch (err) {
                console.error('Error fetching audio:', err);
                setError('Failed to load audio');
            } finally {
                setLoading(false);
            }
        };

        const fetchComments = async () => {
            try {
                setLoadingComments(true);
                const response = await api.get(`/upload/${uploadId}/comments`);
                
                if (response.data.success) {
                    setComments(response.data.data);
                }
            } catch (err) {
                console.error('Error fetching comments:', err);
            } finally {
                setLoadingComments(false);
            }
        };

        if (uploadId) {
            fetchAudio();
            fetchComments();
        }
    }, [uploadId, isAuthenticated]);

    useEffect(() => {
        const audioElement = audioRef.current;
        if (!audioElement) return;

        const updateTime = () => setCurrentTime(audioElement.currentTime);
        const updateDuration = () => setDuration(audioElement.duration);
        const handleEnded = () => setIsPlaying(false);

        audioElement.addEventListener('timeupdate', updateTime);
        audioElement.addEventListener('loadedmetadata', updateDuration);
        audioElement.addEventListener('ended', handleEnded);

        return () => {
            audioElement.removeEventListener('timeupdate', updateTime);
            audioElement.removeEventListener('loadedmetadata', updateDuration);
            audioElement.removeEventListener('ended', handleEnded);
        };
    }, [audio]);

    const togglePlayPause = () => {
        const audioElement = audioRef.current;
        if (isPlaying) {
            audioElement.pause();
            trackingService.stopPlayTracking(uploadId);
        } else {
            audioElement.play();
            trackingService.startPlayTracking(uploadId, audioElement);
            // Update play count immediately for UI feedback
            setPlayCount(prev => prev + 1);
        }
        setIsPlaying(!isPlaying);
    };

    const handleSeek = (e) => {
        const audioElement = audioRef.current;
        const rect = e.target.getBoundingClientRect();
        const percent = (e.clientX - rect.left) / rect.width;
        const newTime = percent * duration;
        audioElement.currentTime = newTime;
        setCurrentTime(newTime);
    };

    const handleVolumeChange = (e) => {
        const newVolume = parseFloat(e.target.value);
        setVolume(newVolume);
        audioRef.current.volume = newVolume;
    };

    const skipTime = (seconds) => {
        const audioElement = audioRef.current;
        audioElement.currentTime = Math.max(0, Math.min(duration, audioElement.currentTime + seconds));
    };

    const handleDownloadClick = () => {
        setDownloadPaywall({ isOpen: true, audio });
    };

    const formatTime = (time) => {
        const minutes = Math.floor(time / 60);
        const seconds = Math.floor(time % 60);
        return `${minutes}:${seconds.toString().padStart(2, '0')}`;
    };

    const handleShare = () => {
        setShareModal({ isOpen: true, audio });
    };

    const handleLike = async () => {
        if (!isAuthenticated) {
            setSignupInvite({ isOpen: true, contentType: 'audio' });
            return;
        }

        try {
            const response = await api.post(`/upload/${uploadId}/like`);
            
            if (response.data.success) {
                setAudio(prev => ({
                    ...prev,
                    like_count: response.data.data.likeCount,
                    user_has_liked: response.data.data.liked
                }));
            }
        } catch (err) {
            console.error('Error toggling like:', err);
        }
    };

    const handleCommentLike = async (commentId) => {
        if (!isAuthenticated) {
            setSignupInvite({ isOpen: true, contentType: 'audio' });
            return;
        }

        try {
            const response = await api.post(`/upload/comments/${commentId}/like`);
            
            if (response.data.success) {
                setComments(prev => prev.map(comment => 
                    comment.id === commentId
                        ? { ...comment, like_count: response.data.data.likeCount, user_has_liked: response.data.data.liked }
                        : comment
                ));
            }
        } catch (err) {
            console.error('Error toggling comment like:', err);
        }
    };

    const handleAddComment = async (e) => {
        e.preventDefault();
        if (!newComment.trim() || !isAuthenticated) return;

        try {
            setSubmittingComment(true);
            const response = await api.post(`/upload/${uploadId}/comments`, {
                content: newComment.trim()
            });
            
            if (response.data.success) {
                setComments(prev => [response.data.data, ...prev]);
                setNewComment('');
                setAudio(prev => ({
                    ...prev,
                    comment_count: (prev.comment_count || 0) + 1
                }));
            }
        } catch (err) {
            console.error('Error adding comment:', err);
        } finally {
            setSubmittingComment(false);
        }
    };

    const formatTimeAgo = (dateString) => {
        const date = new Date(dateString);
        const now = new Date();
        const diffInSeconds = Math.floor((now - date) / 1000);

        if (diffInSeconds < 60) return 'Just now';
        if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
        if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
        if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)}d ago`;
        if (diffInSeconds < 2592000) return `${Math.floor(diffInSeconds / 604800)}w ago`;
        return date.toLocaleDateString();
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-900 flex items-center justify-center">
                <div className="text-white text-lg">Loading audio...</div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen bg-gray-900 flex items-center justify-center">
                <div className="text-center">
                    <div className="text-red-400 text-lg mb-4">{error}</div>
                    <button 
                        onClick={() => navigate('/')}
                        className="text-purple-400 hover:text-purple-300"
                    >
                        Go back to home
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900/20 to-gray-900 text-white">
            {/* Hidden Audio Element */}
            <audio ref={audioRef} src={audio?.file_url} preload="metadata" controlsList="nodownload" />

            {/* Header */}
            <div className="bg-gray-800/50 backdrop-blur-sm border-b border-gray-700 p-4">
                <div className="max-w-4xl mx-auto flex items-center justify-between">
                    <button
                        onClick={() => navigate(-1)}
                        className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors"
                    >
                        <ArrowLeft className="w-5 h-5" />
                        Back
                    </button>
                    
                    <div className="flex items-center gap-4">
                        <button
                            onClick={handleLike}
                            className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-colors ${
                                audio?.user_has_liked
                                    ? 'text-red-400 bg-red-400/10 hover:bg-red-400/20' 
                                    : 'text-gray-400 hover:text-red-400 hover:bg-red-400/10'
                            }`}
                            title={isAuthenticated 
                                ? (audio?.user_has_liked ? 'Unlike this track' : 'Like this track')
                                : 'Sign up to like tracks and support artists'
                            }
                        >
                            <Heart className={`w-4 h-4 ${audio?.user_has_liked ? 'fill-current' : ''}`} />
                            {audio?.like_count || 0}
                        </button>
                        <button
                            onClick={handleShare}
                            className="flex items-center gap-2 px-3 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg transition-colors"
                            title="Share"
                        >
                            <Share2 className="w-4 h-4" />
                            Share
                        </button>
                        <button
                            onClick={handleDownloadClick}
                            className="flex items-center gap-2 px-3 py-2 bg-purple-600 hover:bg-purple-700 rounded-lg transition-colors"
                        >
                            <Download className="w-4 h-4" />
                            Download
                        </button>
                    </div>
                </div>
            </div>

            {/* Main Content */}
            <div className="max-w-4xl mx-auto p-6">
                {/* Album Art / Visualizer Area */}
                <div className="text-center mb-8">
                    <div className="w-80 h-80 mx-auto bg-gradient-to-br from-purple-500 to-pink-500 rounded-full flex items-center justify-center mb-6 shadow-2xl">
                        <div className="w-60 h-60 bg-gray-800 rounded-full flex items-center justify-center">
                            <Volume2 className="w-20 h-20 text-purple-400" />
                        </div>
                    </div>
                    
                    {/* Track Info */}
                    <h1 className="text-4xl font-bold mb-2">{audio?.title}</h1>
                    <p className="text-xl text-gray-400 mb-4">
                        by {audio?.display_name || audio?.username}
                    </p>
                </div>

                {/* Audio Controls */}
                <div className="bg-gray-800/50 rounded-lg p-6 mb-6">
                    {/* Progress Bar */}
                    <div className="mb-6">
                        <div 
                            className="w-full h-2 bg-gray-600 rounded-full cursor-pointer relative"
                            onClick={handleSeek}
                        >
                            <div 
                                className="h-full bg-purple-500 rounded-full"
                                style={{ width: `${(currentTime / duration) * 100 || 0}%` }}
                            />
                        </div>
                        <div className="flex justify-between text-sm text-gray-400 mt-2">
                            <span>{formatTime(currentTime)}</span>
                            <span>{formatTime(duration)}</span>
                        </div>
                    </div>

                    {/* Playback Controls */}
                    <div className="flex items-center justify-center gap-6 mb-4">
                        <button
                            onClick={() => skipTime(-15)}
                            className="w-12 h-12 bg-gray-700 hover:bg-gray-600 rounded-full flex items-center justify-center transition-colors"
                        >
                            <SkipBack className="w-6 h-6" />
                        </button>
                        
                        <button
                            onClick={togglePlayPause}
                            className="w-16 h-16 bg-purple-600 hover:bg-purple-700 rounded-full flex items-center justify-center transition-colors"
                        >
                            {isPlaying ? (
                                <Pause className="w-8 h-8" />
                            ) : (
                                <Play className="w-8 h-8 ml-1" />
                            )}
                        </button>
                        
                        <button
                            onClick={() => skipTime(15)}
                            className="w-12 h-12 bg-gray-700 hover:bg-gray-600 rounded-full flex items-center justify-center transition-colors"
                        >
                            <SkipForward className="w-6 h-6" />
                        </button>
                    </div>

                    {/* Volume Control */}
                    <div className="flex items-center justify-center gap-4">
                        <Volume2 className="w-5 h-5 text-gray-400" />
                        <input
                            type="range"
                            min="0"
                            max="1"
                            step="0.1"
                            value={volume}
                            onChange={handleVolumeChange}
                            className="w-32"
                        />
                    </div>
                </div>

                {/* Track Details */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Left Column - Description */}
                    <div className="space-y-6">
                        <div className="bg-gray-800/50 rounded-lg p-6">
                            <h3 className="text-lg font-semibold text-gray-300 mb-2">Description</h3>
                            <RichDescription
                                description={audio?.description}
                                maxLength={250}
                                showLabel={false}
                                textClassName="text-gray-300"
                            />
                        </div>
                        
                        {/* Genres */}
                        {audio?.genre && (
                            <div className="bg-gray-800/50 rounded-lg p-6">
                                <h3 className="text-lg font-semibold text-gray-300 mb-3">Genres</h3>
                                <div className="flex flex-wrap gap-2">
                                    {audio.genre.split(',').map((genre, index) => (
                                        <span key={index} className="px-3 py-1 bg-purple-600/20 text-purple-300 rounded-full text-sm">
                                            {genre.trim()}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Contributing Artists */}
                        {audio?.id && (
                            <ContributingArtistsDisplay
                                mediaType="upload"
                                mediaId={audio.id}
                                layout="horizontal"
                                maxVisible={5}
                                className="bg-gray-800/50 rounded-lg p-6"
                            />
                        )}
                    </div>
                    
                    {/* Right Column - Details */}
                    <div className="bg-gray-800/50 rounded-lg p-6">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="font-semibold text-gray-300">Track Details</h3>
                            {/* Edit Button - Only show for track owner */}
                            {/* TODO: Add edit functionality */}
                        </div>
                        
                        <div className="space-y-4">
                            {/* Music Metadata - Prominent Display */}
                            {audio?.artist && (
                                <div className="bg-gray-900/50 rounded-lg p-4">
                                    <div className="flex justify-between items-center">
                                        <span className="text-gray-400 text-sm font-medium">Artist</span>
                                        <span className="text-white font-semibold">{audio.artist}</span>
                                    </div>
                                </div>
                            )}
                            
                            {audio?.album && (
                                <div className="bg-gray-900/50 rounded-lg p-4">
                                    <div className="flex justify-between items-center">
                                        <span className="text-gray-400 text-sm font-medium">Album</span>
                                        <span className="text-white font-semibold">{audio.album}</span>
                                    </div>
                                </div>
                            )}
                            
                            {audio?.year && (
                                <div className="bg-gray-900/50 rounded-lg p-4">
                                    <div className="flex justify-between items-center">
                                        <span className="text-gray-400 text-sm font-medium">Year</span>
                                        <span className="text-white font-semibold">{audio.year}</span>
                                    </div>
                                </div>
                            )}

                            {(audio?.bpm || audio?.key_signature || audio?.track_number) && (
                                <div className="bg-gray-900/50 rounded-lg p-4 space-y-3">
                                    <h4 className="text-gray-300 font-medium text-sm">Technical Info</h4>
                                    {audio?.bpm && (
                                        <div className="flex justify-between">
                                            <span className="text-gray-400 text-sm">BPM:</span>
                                            <span className="text-white">{audio.bpm}</span>
                                        </div>
                                    )}
                                    {audio?.key_signature && (
                                        <div className="flex justify-between">
                                            <span className="text-gray-400 text-sm">Key:</span>
                                            <span className="text-white">{audio.key_signature}</span>
                                        </div>
                                    )}
                                    {audio?.track_number && (
                                        <div className="flex justify-between">
                                            <span className="text-gray-400 text-sm">Track #:</span>
                                            <span className="text-white">{audio.track_number}</span>
                                        </div>
                                    )}
                                    {audio?.duration && (
                                        <div className="flex justify-between">
                                            <span className="text-gray-400 text-sm">Duration:</span>
                                            <span className="text-white">{Math.floor(audio.duration / 60)}:{(audio.duration % 60).toString().padStart(2, '0')}</span>
                                        </div>
                                    )}
                                </div>
                            )}
                            
                            {/* File Info */}
                            <div className="bg-gray-900/50 rounded-lg p-4 space-y-3">
                                <h4 className="text-gray-300 font-medium text-sm">File Info</h4>
                                <div className="flex justify-between">
                                    <span className="text-gray-400 text-sm">Format:</span>
                                    <span className="text-white">{audio?.mime_type?.split('/')[1]?.toUpperCase() || '-'}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-gray-400 text-sm">Size:</span>
                                    <span className="text-white">{audio?.file_size ? (audio.file_size / 1024 / 1024).toFixed(1) + ' MB' : '-'}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-gray-400 text-sm">Uploaded:</span>
                                    <span className="text-white">{audio?.created_at ? new Date(audio.created_at).toLocaleDateString() : '-'}</span>
                                </div>
                            </div>
                            
                            {/* Engagement Stats */}
                            <div className="bg-gray-900/50 rounded-lg p-4 space-y-3">
                                <h4 className="text-gray-300 font-medium text-sm">Stats</h4>
                                <div className="flex justify-between">
                                    <span className="text-gray-400 text-sm">Plays:</span>
                                    <span className="text-white font-semibold">{playCount.toLocaleString()}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-gray-400 text-sm">Likes:</span>
                                    <span className="text-white font-semibold">{audio?.like_count || 0}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-gray-400 text-sm">Visibility:</span>
                                    <span className="text-white">{audio?.is_public ? 'Public' : 'Private'}</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="bg-gray-800/50 rounded-lg p-6">
                        <h3 className="font-semibold mb-4 text-gray-300">Artist Information</h3>
                        <div className="flex items-center gap-4 mb-4">
                            <div className="w-16 h-16 bg-purple-500 rounded-full flex items-center justify-center">
                                {audio?.profile_picture_url ? (
                                    <img
                                        src={audio.profile_picture_url}
                                        alt={audio.display_name}
                                        width="64"
                                        height="64"
                                        className="w-16 h-16 rounded-full object-cover"
                                    />
                                ) : (
                                    <span className="text-white font-semibold text-xl">
                                        {audio?.display_name?.charAt(0) || audio?.username?.charAt(0) || 'U'}
                                    </span>
                                )}
                            </div>
                            <div>
                                <p className="font-semibold text-lg">{audio?.display_name || audio?.username}</p>
                                <p className="text-gray-400">@{audio?.username}</p>
                            </div>
                        </div>
                        
                        {/* Contributing Artists */}
                        {audio?.id && (
                            <div className="mb-4">
                                <ContributingArtistsDisplay
                                    mediaType="upload"
                                    mediaId={audio.id}
                                    layout="horizontal"
                                    maxVisible={3}
                                    className="text-sm"
                                />
                            </div>
                        )}
                        
                        {/* Engagement Stats */}
                        <div className="flex items-center gap-6">
                            <button
                                onClick={handleLike}
                                className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                                    audio?.user_has_liked
                                        ? 'text-red-400 bg-red-400/10 hover:bg-red-400/20' 
                                        : 'text-gray-400 hover:text-red-400 hover:bg-red-400/10'
                                }`}
                            >
                                <Heart className={`w-5 h-5 ${audio?.user_has_liked ? 'fill-current' : ''}`} />
                                <span>{audio?.like_count || 0}</span>
                            </button>

                            <div className="flex items-center gap-2 text-gray-400">
                                <MessageCircle className="w-5 h-5" />
                                <span>{audio?.comment_count || 0}</span>
                            </div>
                        </div>
                    </div>
                </div>
                
                {/* Artist Profile Section */}
                <div className="bg-gray-800/50 rounded-lg p-6 mb-8">
                    <h3 className="font-semibold mb-4 text-gray-300">Artist</h3>
                    <div className="flex items-center gap-4">
                        <div className="w-16 h-16 bg-purple-500 rounded-full flex items-center justify-center">
                            {audio?.profile_picture_url ? (
                                <img
                                    src={audio.profile_picture_url}
                                    alt={audio.display_name}
                                    width="64"
                                    height="64"
                                    className="w-16 h-16 rounded-full object-cover"
                                />
                            ) : (
                                <span className="text-white font-semibold text-xl">
                                    {audio?.display_name?.charAt(0) || audio?.username?.charAt(0) || 'U'}
                                </span>
                            )}
                        </div>
                        <div className="flex-1">
                            <p className="font-semibold text-lg">{audio?.display_name || audio?.username}</p>
                            <p className="text-gray-400">@{audio?.username}</p>
                        </div>
                        <div className="flex items-center gap-6">
                            <button
                                onClick={handleLike}
                                className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                                    audio?.user_has_liked
                                        ? 'text-red-400 bg-red-400/10 hover:bg-red-400/20' 
                                        : 'text-gray-400 hover:text-red-400 hover:bg-red-400/10'
                                }`}
                            >
                                <Heart className={`w-5 h-5 ${audio?.user_has_liked ? 'fill-current' : ''}`} />
                                <span>{audio?.like_count || 0}</span>
                            </button>

                            <div className="flex items-center gap-2 text-gray-400">
                                <MessageCircle className="w-5 h-5" />
                                <span>{audio?.comment_count || 0}</span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Comments Section */}
                <div className="mt-8">
                    <h3 className="text-xl font-bold text-white mb-6">
                        Comments ({audio?.comment_count || 0})
                    </h3>

                    {/* Add Comment Form */}
                    {isAuthenticated ? (
                        <div className="bg-gray-800/50 rounded-lg p-6 mb-6">
                            <form onSubmit={handleAddComment}>
                                <textarea
                                    value={newComment}
                                    onChange={(e) => setNewComment(e.target.value)}
                                    placeholder="Add a comment..."
                                    className="w-full bg-gray-800 border border-gray-600 rounded-lg p-4 text-white placeholder-gray-400 resize-none focus:border-purple-500 focus:ring-1 focus:ring-purple-500 transition-colors"
                                    rows="3"
                                />
                                <div className="flex justify-end mt-4">
                                    <button
                                        type="submit"
                                        disabled={!newComment.trim() || submittingComment}
                                        className="px-6 py-2 bg-purple-600 hover:bg-purple-700 disabled:bg-gray-600 disabled:opacity-50 text-white rounded-lg transition-colors"
                                    >
                                        {submittingComment ? 'Posting...' : 'Post Comment'}
                                    </button>
                                </div>
                            </form>
                        </div>
                    ) : (
                        <div className="bg-gray-800/50 rounded-lg p-6 mb-6 text-center">
                            <div className="text-4xl mb-4">💬</div>
                            <h3 className="text-xl font-bold text-white mb-2">Join the conversation</h3>
                            <p className="text-gray-400 mb-6">
                                Login to share your thoughts and connect with other members of The Deep Wave community.
                            </p>
                            <div className="flex flex-col sm:flex-row gap-3 justify-center">
                                <button
                                    onClick={() => navigate('/login')}
                                    className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors font-medium"
                                >
                                    Login to Comment
                                </button>
                                <button
                                    onClick={() => navigate('/register')}
                                    className="px-6 py-3 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors font-medium"
                                >
                                    Create Account
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Comments List */}
                    <div className="space-y-4">
                        {loadingComments ? (
                            <div className="text-center py-8">
                                <div className="loading-spinner w-8 h-8 mx-auto mb-4"></div>
                                <p className="text-gray-400">Loading comments...</p>
                            </div>
                        ) : comments.length > 0 ? (
                            comments.map((comment) => (
                                <div key={comment.id} className="bg-gray-800/30 rounded-lg p-6">
                                    <div className="flex items-start gap-4">
                                        <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-700 rounded-full flex items-center justify-center flex-shrink-0">
                                            {comment.avatar ? (
                                                <img
                                                    src={comment.avatar}
                                                    alt={comment.display_name}
                                                    width="40"
                                                    height="40"
                                                    className="w-10 h-10 rounded-full object-cover"
                                                />
                                            ) : (
                                                <span className="text-white font-semibold text-sm">
                                                    {(comment.display_name || comment.username)?.charAt(0) || 'U'}
                                                </span>
                                            )}
                                        </div>
                                        <div className="flex-1">
                                            <div className="flex items-center gap-2 mb-2">
                                                <h4 className="font-semibold text-white">
                                                    {comment.display_name || comment.username}
                                                </h4>
                                                <span className="text-sm text-gray-500">
                                                    {formatTimeAgo(comment.created_at)}
                                                </span>
                                            </div>
                                            <p className="text-gray-300 leading-relaxed mb-3">
                                                {comment.content}
                                            </p>
                                            <button
                                                onClick={() => handleCommentLike(comment.id)}
                                                className={`flex items-center gap-2 px-3 py-1 rounded-lg transition-colors text-sm ${
                                                    comment.user_has_liked
                                                        ? 'text-red-400 bg-red-400/10 hover:bg-red-400/20' 
                                                        : 'text-gray-400 hover:text-red-400 hover:bg-red-400/10'
                                                }`}
                                            >
                                                <Heart className={`w-4 h-4 ${comment.user_has_liked ? 'fill-current' : ''}`} />
                                                <span>{comment.like_count || 0}</span>
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            ))
                        ) : (
                            <div className="text-center py-8">
                                <MessageCircle className="w-12 h-12 text-gray-600 mx-auto mb-4" />
                                <p className="text-gray-400">No comments yet. Be the first to comment!</p>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Download Paywall Modal */}
            <DownloadPaywall
                isOpen={downloadPaywall.isOpen}
                onClose={() => setDownloadPaywall({ isOpen: false, audio: null })}
                mediaTitle={downloadPaywall.audio?.title || 'Unknown Track'}
                mediaType="song"
                uploadId={downloadPaywall.audio?.id}
            />

            {/* Share Modal */}
            <ShareModal
                isOpen={shareModal.isOpen}
                onClose={() => setShareModal({ isOpen: false, audio: null })}
                mediaTitle={shareModal.audio?.title || 'Unknown Track'}
                mediaType="audio"
                username={shareModal.audio?.username}
                mediaId={shareModal.audio?.stored_filename || shareModal.audio?.original_filename || shareModal.audio?.id}
                description={shareModal.audio?.description}
                artistName={shareModal.audio?.display_name || shareModal.audio?.username}
                imageUrl={shareModal.audio?.cover_art_url}
                duration={shareModal.audio?.duration ? formatTime(shareModal.audio.duration) : null}
                tags={shareModal.audio?.tags}
            />

            {/* Signup Invite Modal */}
            <SignupInviteModal
                isOpen={signupInvite.isOpen}
                onClose={() => setSignupInvite({ isOpen: false, contentType: 'audio' })}
                contentType={signupInvite.contentType}
            />
        </div>
    );
};

export default AudioViewer;